import React, { useState } from 'react';
import { supportApi, encryptToJwe, sha256Hex } from '../services/supportApi';

const TENANT_ID = '9e7f6722-c79b-487b-909f-ad508dc76e1c';
const TENANT_NAME = 'AL Conseil';

type Stage = 'compose' | 'collected' | 'submitting' | 'submitted' | 'error';

export function SupportSubmit({ userUpn }: { userUpn: string }) {
  const [contactEmail, setContactEmail] = useState(userUpn || '');
  const [summary, setSummary] = useState('');
  const [problem, setProblem] = useState('');
  const [stage, setStage] = useState<Stage>('compose');
  const [bundle, setBundle] = useState<{ markdown: string; piiMap: any[]; stats: any } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ticketResult, setTicketResult] = useState<{ ticketNumber: string; ticketId: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleCollect() {
    setError(null);
    if (!contactEmail || !summary || !problem) {
      setError('Email, résumé et description sont obligatoires.');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(contactEmail)) {
      setError('Email contact invalide.');
      return;
    }
    try {
      setBusy(true);
      const res = await supportApi.collectDiagnostics(TENANT_ID, TENANT_NAME, userUpn, problem, 'fr-FR');
      setBundle(res);
      setStage('collected');
    } catch (e: any) {
      setError(`Échec collecte diagnostic : ${e.message}`);
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit() {
    if (!bundle) return;
    try {
      setBusy(true);
      setStage('submitting');
      const { jweBundle, jweKeyId } = await encryptToJwe(bundle.markdown);
      const upnHashHex = await sha256Hex(userUpn);
      const upnHash = `user_${upnHashHex.substring(0, 12)}`;

      const res = await supportApi.submitTicket({
        tenantId: TENANT_ID,
        tenantDisplayName: TENANT_NAME,
        createdByUpnHash: upnHash,
        contactEmail,
        summary,
        jweBundle,
        jweKeyId,
        piiMap: bundle.piiMap,
      });
      setTicketResult(res);
      setStage('submitted');
    } catch (e: any) {
      setError(`Échec soumission : ${e.message}`);
      setStage('error');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setStage('compose');
    setBundle(null);
    setTicketResult(null);
    setError(null);
    setSummary('');
    setProblem('');
  }

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ background: '#0078D4', color: '#fff', padding: '20px 24px', borderRadius: 2, marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Support CLARA</h2>
        <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>
          Soumettre un ticket à l'équipe Alceai. Diagnostic chiffré bout-en-bout (JWE).
        </div>
      </div>

      {stage === 'compose' && (
        <div style={{ background: '#fff', border: '1px solid #edebe9', padding: 24, borderRadius: 2 }}>
          <Field label="Email de contact (obligatoire — utilisé pour les retours support)">
            <input
              type="email"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              placeholder="vous@votre-domaine.com"
              style={inputStyle}
            />
          </Field>
          <Field label="Résumé court (visible dans le portail admin Alceai)">
            <input
              type="text"
              value={summary}
              maxLength={250}
              onChange={e => setSummary(e.target.value)}
              placeholder="Ex : Reset PIN bloqué pour 12 utilisateurs ce matin"
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: '#a19f9d', marginTop: 4 }}>{summary.length}/250</div>
          </Field>
          <Field label="Description détaillée (sera incluse dans le bundle chiffré)">
            <textarea
              value={problem}
              onChange={e => setProblem(e.target.value)}
              placeholder="Décrivez le contexte, les symptômes, ce que vous avez essayé. Évitez d'écrire ici des emails ou identifiants — ils seront automatiquement masqués."
              rows={8}
              style={{ ...inputStyle, fontFamily: 'inherit' }}
            />
          </Field>
          {error && <div style={errorBox}>{error}</div>}
          <button onClick={handleCollect} disabled={busy} style={primaryBtn}>
            {busy ? 'Collecte en cours...' : 'Collecter les diagnostics →'}
          </button>
        </div>
      )}

      {stage === 'collected' && bundle && (
        <div style={{ background: '#fff', border: '1px solid #edebe9', padding: 24, borderRadius: 2 }}>
          <div style={{ background: '#f3f9fd', border: '1px solid #c7e0f4', borderRadius: 2, padding: '12px 16px', marginBottom: 16 }}>
            <strong style={{ color: '#0078D4' }}>Diagnostic prêt</strong>
            <div style={{ fontSize: 12, marginTop: 4, color: '#605e5c' }}>
              {bundle.stats.markdownBytes} octets · {bundle.stats.piiHashedCount} identifiants hashés ·{' '}
              {bundle.stats.failedOps} opérations en échec sur 24h
            </div>
          </div>

          <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: '#605e5c', fontWeight: 600, marginBottom: 8 }}>
            Aperçu du bundle (Markdown brut)
          </div>
          <pre style={{
            background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 2, padding: 16,
            fontSize: 11, lineHeight: 1.5, maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0,
          }}>
            {bundle.markdown}
          </pre>

          <div style={{ fontSize: 12, color: '#a19f9d', marginTop: 12, marginBottom: 16 }}>
            En cliquant sur Soumettre, ce contenu sera chiffré avec la clé publique RSA-4096 d'Alceai. Seul le Key Vault Alceai peut le déchiffrer, et chaque accès est journalisé.
          </div>

          {error && <div style={errorBox}>{error}</div>}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setStage('compose')} style={secondaryBtn}>← Modifier</button>
            <button onClick={handleSubmit} disabled={busy} style={primaryBtn}>
              {busy ? 'Chiffrement + envoi...' : 'Chiffrer et soumettre'}
            </button>
          </div>
        </div>
      )}

      {stage === 'submitting' && (
        <div style={{ background: '#fff', border: '1px solid #edebe9', padding: 40, borderRadius: 2, textAlign: 'center' }}>
          <div style={{ fontSize: 14, color: '#605e5c' }}>Chiffrement et envoi en cours...</div>
        </div>
      )}

      {stage === 'submitted' && ticketResult && (
        <div style={{ background: '#fff', border: '1px solid #edebe9', padding: 32, borderRadius: 2 }}>
          <div style={{ background: '#dff6dd', border: '1px solid #92c353', borderRadius: 2, padding: '16px 20px', marginBottom: 20 }}>
            <div style={{ color: '#107c10', fontWeight: 600, fontSize: 16 }}>✓ Ticket créé</div>
            <div style={{ marginTop: 8, fontSize: 14, color: '#323130' }}>
              Numéro : <strong>{ticketResult.ticketNumber}</strong>
            </div>
            <div style={{ fontSize: 13, color: '#605e5c', marginTop: 6 }}>
              Notre équipe support a été notifiée. Vous recevrez un email à <strong>{contactEmail}</strong> dès qu'un agent prend en charge votre demande ou s'il a besoin d'informations complémentaires.
            </div>
          </div>
          <button onClick={reset} style={secondaryBtn}>Soumettre un autre ticket</button>
        </div>
      )}

      {stage === 'error' && (
        <div style={{ background: '#fff', border: '1px solid #edebe9', padding: 24, borderRadius: 2 }}>
          <div style={errorBox}>{error}</div>
          <button onClick={() => setStage('collected')} style={secondaryBtn}>Réessayer</button>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#323130', marginBottom: 6 }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid #8a8886', borderRadius: 2,
  fontSize: 14, fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif', boxSizing: 'border-box',
};
const primaryBtn: React.CSSProperties = {
  background: '#0078D4', color: '#fff', border: 'none', padding: '10px 20px',
  borderRadius: 2, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const secondaryBtn: React.CSSProperties = {
  background: '#fff', color: '#323130', border: '1px solid #8a8886', padding: '10px 20px',
  borderRadius: 2, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
const errorBox: React.CSSProperties = {
  background: '#fde7e9', border: '1px solid #d13438', color: '#a4262c',
  padding: '10px 14px', borderRadius: 2, fontSize: 13, marginBottom: 12,
};
